/**
 * The snapshot cache.
 *
 * Mirrors `LocalData+Cache.swift`: the last successful response for a handful of
 * screens is kept in IndexedDB and painted immediately on launch, then revalidated.
 * A student opening the app on a train sees their plan rather than a spinner.
 *
 * IndexedDB rather than localStorage for one reason that matters: the AI Solve
 * history holds base64 images and blows straight through localStorage's ~5 MB, and
 * the failure mode there is a thrown `QuotaExceededError` in the middle of a write.
 *
 * This is **not** an offline app shell. There is no service worker and no offline
 * navigation — the cache makes a launch instant and a dropped connection survivable,
 * and it stops there.
 */

import { del, get, set } from 'idb-keyval'

/** Bumped when a cached shape changes. Old entries are ignored rather than
 *  migrated — a stale snapshot is worth exactly one revalidation. */
const VERSION = 1

export interface Snapshot<T> {
  version: number
  savedAt: number
  userId: string
  data: T
}

/** Namespaced by user: a shared laptop must never paint one student's plan for
 *  another, even for the frame before the fetch lands. */
function key(name: string, userId: string): string {
  return `modulus.snapshot.${name}.${userId}`
}

export async function readSnapshot<T>(name: string, userId: string): Promise<T | null> {
  try {
    const snapshot = await get<Snapshot<T>>(key(name, userId))
    if (!snapshot || snapshot.version !== VERSION || snapshot.userId !== userId) return null
    return snapshot.data
  } catch {
    // A browser with IndexedDB disabled, or private mode. The app still works;
    // it just always waits for the network.
    return null
  }
}

export async function writeSnapshot<T>(name: string, userId: string, data: T): Promise<void> {
  try {
    await set(key(name, userId), { version: VERSION, savedAt: Date.now(), userId, data })
  } catch {
    /* a snapshot that cannot be written is not worth failing a render over */
  }
}

export async function clearSnapshot(name: string, userId: string): Promise<void> {
  try {
    await del(key(name, userId))
  } catch {
    /* nothing to do */
  }
}

/** Called on sign-out. Leaving a snapshot behind on a shared machine is the kind
 *  of thing that is only noticed once. */
export async function clearAllSnapshots(userId: string): Promise<void> {
  const names = ['home', 'planner', 'timetable', 'profile', 'subjects', 'tests', 'chats']
  await Promise.all(names.map((name) => clearSnapshot(name, userId)))
}
